// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { SelectField } from "components/ui/select-field";
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  BarChart3,
  LineChart,
  Lightbulb,
  ChevronDown,
  ExternalLink,
  Zap
} from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PerformanceWidget = ({ config, onDrillDown }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetrics, setSelectedMetrics] = useState(['openRates', 'clickRates', 'conversionRates']);
  const [chartType, setChartType] = useState('line');
  const [showInsights, setShowInsights] = useState(true);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  // Time range options
  const timeRangeOptions = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Available metrics
  const availableMetrics = [
    {
      key: 'openRates',
      label: 'Open Rate',
      color: '#3B82F6',
      format: 'percentage'
    },
    {
      key: 'clickRates',
      label: 'Click Rate',
      color: '#10B981',
      format: 'percentage'
    },
    {
      key: 'conversionRates',
      label: 'Conversion Rate',
      color: '#F59E0B',
      format: 'percentage'
    },
    {
      key: 'revenue',
      label: 'Revenue',
      color: '#EF4444',
      format: 'currency'
    }
  ];

  // Load chart data from API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        // TODO: Replace with actual API call
        // const response = await fetch(`/api/dashboard/performance?range=${timeRange}`);
        // const data = await response.json();

        // For now, set empty data until API is implemented
        setChartData(null);
        setInsights([]);
      } catch (error) {
        console.error('Failed to load performance data:', error);
        setChartData(null);
        setInsights([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [timeRange, customDateRange]);

  // Handle metric toggle
  const toggleMetric = (metricKey) => {
    setSelectedMetrics(prev =>
      prev.includes(metricKey)
        ? prev.filter(m => m !== metricKey)
        : [...prev, metricKey]
    );
  };

  // Handle chart data point click for drill-down
  const handleChartClick = (event, elements) => {
    if (elements.length > 0 && onDrillDown) {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const dataIndex = element.index;

      const metric = availableMetrics.find(m =>
        selectedMetrics[datasetIndex] === m.key
      );

      const date = chartData.labels[dataIndex];
      const value = chartData.data[selectedMetrics[datasetIndex]][dataIndex];

      onDrillDown({
        metric: metric?.label || 'Unknown',
        date,
        value,
        timeRange
      });
    }
  };

  // Prepare chart data for Chart.js
  const prepareChartData = () => {
    if (!chartData) return null;

    const datasets = selectedMetrics.map(metricKey => {
      const metric = availableMetrics.find(m => m.key === metricKey);
      if (!metric) return null;

      return {
        label: metric.label,
        data: chartData.data[metricKey],
        borderColor: metric.color,
        backgroundColor: chartType === 'line'
          ? `${metric.color}20`
          : metric.color,
        fill: chartType === 'line',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: metric.color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      };
    }).filter(Boolean);

    return {
      labels: chartData.labels,
      datasets
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context) {
            const metric = availableMetrics.find(m => m.key === selectedMetrics[context.datasetIndex]);
            const value = context.parsed.y;
            const formattedValue = metric?.format === 'percentage'
              ? `${value.toFixed(1)}%`
              : metric?.format === 'currency'
                ? `$${value.toLocaleString()}`
                : value.toFixed(2);

            return `${context.dataset.label}: ${formattedValue}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxTicksLimit: 8
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function (value) {
            // Format y-axis based on selected metrics
            const hasPercentage = selectedMetrics.some(key =>
              availableMetrics.find(m => m.key === key)?.format === 'percentage'
            );
            const hasCurrency = selectedMetrics.some(key =>
              availableMetrics.find(m => m.key === key)?.format === 'currency'
            );

            if (hasPercentage && !hasCurrency) {
              return `${value}%`;
            } else if (hasCurrency && !hasPercentage) {
              return `$${value}`;
            }
            return value;
          }
        }
      }
    },
    onClick: handleChartClick
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-content-secondary">Loading performance data...</p>
        </div>
      </div>
    );
  }

  const chartDataForRender = prepareChartData();

  return (
    <div className="space-y-4">
      {/* Controls Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-content-muted" />
          <div className="relative">
            <SelectField
              value={timeRange}
              onChange={(e) => {
                setTimeRange(e.target.value);
                if (e.target.value === 'custom') {
                  setShowCustomDatePicker(true);
                } else {
                  setShowCustomDatePicker(false);
                }
              }}
              className="appearance-none bg-surface border border-line-strong rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {timeRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none" />
          </div>
        </div>

        {/* Chart Type Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setChartType('line')}
            className={`p-2 rounded-lg transition-colors ${chartType === 'line'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
              : 'text-content-muted hover:bg-gray-100 dark:hover:bg-dark-700'
              }`}
            title="Line Chart"
          >
            <LineChart className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`p-2 rounded-lg transition-colors ${chartType === 'bar'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
              : 'text-content-muted hover:bg-gray-100 dark:hover:bg-dark-700'
              }`}
            title="Bar Chart"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Custom Date Range Picker */}
      {showCustomDatePicker && (
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 border">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => {
                // Apply custom date range
                console.log('Applying custom date range:', customDateRange);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Metric Toggles */}
      <div className="flex flex-wrap gap-2">
        {availableMetrics.map(metric => (
          <button
            key={metric.key}
            onClick={() => toggleMetric(metric.key)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${selectedMetrics.includes(metric.key)
              ? 'bg-blue-100 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50'
              : 'bg-gray-100 dark:bg-dark-700 text-content-secondary hover:bg-gray-200 dark:hover:bg-dark-600'
              }`}
          >
            <span>{metric.icon}</span>
            <span>{metric.label}</span>
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: metric.color }}
            ></div>
          </button>
        ))}
      </div>

      {/* Chart Container */}
      <div className="bg-surface rounded-lg border p-4">
        <div className="h-80">
          {chartDataForRender && selectedMetrics.length > 0 ? (
            chartType === 'line' ? (
              <Line
                ref={chartRef}
                data={chartDataForRender}
                options={chartOptions}
              />
            ) : (
              <Bar
                ref={chartRef}
                data={chartDataForRender}
                options={chartOptions}
              />
            )
          ) : (
            <div className="h-full flex items-center justify-center text-content-muted">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 text-content-subtle" />
                <p>Select metrics to display chart</p>
              </div>
            </div>
          )}
        </div>

        {/* Chart Footer */}
        <div className="mt-4 pt-4 border-t border-line">
          <p className="text-xs text-content-muted flex items-center">
            <ExternalLink className="w-3 h-3 mr-1" />
            Click on data points to drill down into detailed analytics
          </p>
        </div>
      </div>

      {/* AI Insights Panel */}
      {showInsights && insights.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 dark:border-purple-800/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-purple-600 dark:text-purple-300" />
              <h3 className="font-semibold text-purple-900 dark:text-purple-300">AI Performance Insights</h3>
            </div>
            <button
              onClick={() => setShowInsights(false)}
              className="text-purple-600 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-300 text-sm"
            >
              Hide
            </button>
          </div>

          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border-l-4 ${insight.type === 'positive'
                  ? 'bg-green-50 dark:bg-green-900/30 border-green-400'
                  : insight.type === 'negative' || insight.priority === 'critical'
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-400'
                    : 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-400'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-sm font-medium ${insight.type === 'positive'
                        ? 'text-green-800 dark:text-green-300'
                        : insight.type === 'negative' || insight.priority === 'critical'
                          ? 'text-red-800 dark:text-red-300'
                          : 'text-yellow-800 dark:text-yellow-300'
                        }`}>
                        {insight.metric}
                      </span>
                      {insight.type === 'positive' && <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-300" />}
                      {(insight.type === 'negative' || insight.priority === 'critical') && <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-300" />}
                      <span className={`text-xs px-2 py-1 rounded-full ${insight.priority === 'critical'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : insight.priority === 'high'
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : insight.priority === 'medium'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        }`}>
                        {insight.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{insight.message}</p>
                    <button className="text-xs text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center space-x-1">
                      <Lightbulb className="w-3 h-3" />
                      <span>{insight.action}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show Insights Toggle (when hidden) */}
      {!showInsights && insights.length > 0 && (
        <button
          onClick={() => setShowInsights(true)}
          className="w-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 py-2 px-4 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/40 transition-colors text-sm flex items-center justify-center space-x-2"
        >
          <Zap className="w-4 h-4" />
          <span>Show AI Insights ({insights.length})</span>
        </button>
      )}
    </div>
  );
};

export default PerformanceWidget;